import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Copy,
  MessageCircle,
  Minus,
  Plus,
  Share2,
  ShieldCheck,
  ShoppingBag,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import ImageGallery from "@/components/store/ImageGallery";
import CartDrawer from "@/components/store/CartDrawer";
import StickyCartButton from "@/components/store/StickyCartButton";
import ProductCard from "@/components/store/ProductCard";
import PolicyCard from "@/components/store/PolicyCard";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useStock } from "@/context/StockContext";
import { useCatalog, isHiddenById } from "@/context/CatalogContext";
import {
  PRODUCTS,
  ALL_PRODUCTS,
  getProductBySlug,
  computeDiscountedPrice,
  formatPrice,
  getProductDiscountPct,
  isProductNew,
  localized,
  productColorEnList,
  localizedColor,
} from "@/data/products";
import { buildWhatsAppUrl, CANONICAL_ORIGIN } from "@/data/translations";
import { trackProductView } from "@/lib/recentlyViewed";
import { trackEvent } from "@/lib/analytics";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang, isRTL } = useLanguage();
  const S = t.store;
  // Re-render this page whenever the catalog overlay refreshes so a product
  // that just got hidden in the admin disappears here too without a reload.
  const { version: catalogVersion } = useCatalog();

  const rawProduct = useMemo(() => getProductBySlug(slug), [slug, catalogVersion]);
  // Treat a hidden product the same as a missing one — the "Not found" page
  // is the canonical dead-end so direct URLs from search engines, shared
  // links or cached tabs stop working immediately after the admin hides it.
  const product = rawProduct && !isHiddenById(rawProduct.id) ? rawProduct : null;
  const { addToCart, openCart, lineQty, variantStockOf, variantRemaining } = useCart();
  const { isProductOutOfStock, isSizeOutOfStock, sizeQty: liveSizeQty, productTotalQty, isColorOutOfStock } = useStock();

  const colorList = useMemo(() => productColorEnList(product), [product]);
  const hasMultiColors = colorList.length > 0;

  const initialSize = (() => {
    if (!product || !Array.isArray(product.sizes) || !product.sizes.length) return "";
    // pick first available size as default (live stock-aware)
    const firstAvail = product.sizes.find((s) => !isSizeOutOfStock(product.id, s.label));
    return firstAvail ? firstAvail.label : product.sizes[0].label;
  })();

  const initialColor = (() => {
    if (!hasMultiColors) return "";
    const firstAvail = colorList.find((c) => !isColorOutOfStock(product.id, c));
    return firstAvail || colorList[0];
  })();

  const [selectedSize, setSelectedSize] = useState(initialSize);
  const [selectedColor, setSelectedColor] = useState(initialColor);
  const [qty, setQty] = useState(1);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const trackedProductRef = useRef("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (typeof window !== "undefined" && slug) {
      setShareUrl(`${CANONICAL_ORIGIN}/stock/${slug}`);
    }
    if (typeof document !== "undefined" && product) {
      const name = localized(product.name, lang);
      document.title = `#${product.id} ${name} — BOB Services LB`;
    }
    if (product?.id) {
      trackProductView(product.id);
      if (trackedProductRef.current !== product.id) {
        trackedProductRef.current = product.id;
        const productPayload = {
          id: product.id,
          title: localized(product.name, lang),
          category: product.category,
          sheinCategory: product.sheinCategory,
        };
        trackEvent("product_detail_view", {
          routeName: "product_detail",
          product: productPayload,
        });
        trackEvent("product_view", {
          routeName: "product_detail",
          product: productPayload,
        });
      }
    }
  }, [slug, lang, product]);

  // Reset quantity to 1 whenever the active variant (size / color) changes
  // so the user never starts with a value greater than the new variant stock.
  useEffect(() => {
    setQty(1);
  }, [selectedSize, selectedColor]);

  // ---- variant + stock derivation (must run on every render to keep hooks
  // ordering stable, even when `product` is missing) ----
  const hasSizes = !!product && Array.isArray(product.sizes) && product.sizes.length > 0;
  const sizeArg = hasSizes ? selectedSize : "";
  const colorArg = hasMultiColors ? selectedColor : "";
  const variantStock = product ? variantStockOf(product.id, sizeArg, colorArg) : 0;
  const inCartForVariant = product ? lineQty(product.id, sizeArg, colorArg) : 0;
  const remainingForVariant = product ? variantRemaining(product.id, sizeArg, colorArg) : 0;

  // Continuously clamp qty against the live remaining-cap so the (+) button
  // can never produce a value the cart cannot accept (e.g., after a refresh
  // when the backend has fewer units than the local picker assumed).
  useEffect(() => {
    if (!product) return;
    setQty((q) => {
      if (remainingForVariant <= 0) return 1;
      return Math.min(q, remainingForVariant);
    });
  }, [remainingForVariant, product]);

  if (!product) {
    return (
      <main className="min-h-screen overflow-x-hidden bg-[#FAFAFA] text-[#111] dark:bg-[#080808] dark:text-[#F6F7FB]">
        <Navbar />
        <section className="px-5 pb-20 pt-32 md:px-8">
          <div className="mx-auto max-w-3xl rounded-[32px] border border-black/6 bg-white px-8 py-16 text-center shadow-[0_24px_60px_rgba(16,24,40,0.08)] dark:border-white/10 dark:bg-[#121212]">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF1E7] text-2xl font-bold text-[#F37021]">
              0
            </div>
            <h1 className="font-display text-3xl font-bold text-[#111] dark:text-white">{S.productNotFound}</h1>
            <p className="mx-auto mt-3 max-w-lg text-base leading-relaxed text-[#666] dark:text-[#BAC4D4]">
              {S.noResultsHint}
            </p>
            <Link
              to="/stock"
              className="mt-7 inline-flex h-12 items-center gap-2 rounded-full bg-[#F37021] px-6 font-medium text-white shadow-[0_14px_30px_rgba(243,112,33,0.24)] transition-all hover:-translate-y-0.5 hover:bg-[#E06015]"
            >
              <ArrowLeft className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
              {S.backToStore}
            </Link>
          </div>
        </section>
        <Footer />
        <FloatingWhatsApp />
      </main>
    );
  }

  const name = localized(product.name, lang);
  const desc = product.description ? localized(product.description, lang) : "";
  // Single-color products (no `colors` array) keep showing their static color
  // label; multi-color products surface a swatch selector instead.
  const color = !hasMultiColors && product.color ? localized(product.color, lang) : "";
  const discountPct = getProductDiscountPct(product);
  const discounted = computeDiscountedPrice(product.originalPrice, discountPct);
  const priceTBD = product.originalPrice == null;
  const available = !isProductOutOfStock(product.id);

  const variantOutOfStock = variantStock <= 0;
  const reachedMaxInCart = remainingForVariant <= 0;

  const canAdd =
    available &&
    !priceTBD &&
    (hasSizes ? !isSizeOutOfStock(product.id, selectedSize) : true) &&
    (hasMultiColors ? !isColorOutOfStock(product.id, selectedColor) : true) &&
    !variantOutOfStock &&
    !reachedMaxInCart &&
    qty <= remainingForVariant;

  const handleAdd = () => {
    const productPayload = {
      id: product.id,
      title: name,
      category: product.category,
      sheinCategory: product.sheinCategory,
    };
    trackEvent("product_add_to_cart_click", {
      routeName: "product_detail",
      product: productPayload,
      metadata: { size: sizeArg || "", color: colorArg || "", quantity: qty },
    });
    if (!canAdd) {
      trackEvent("product_add_to_cart_failed", {
        routeName: "product_detail",
        product: productPayload,
        metadata: {
          size: sizeArg || "",
          color: colorArg || "",
          quantity: qty,
          reason: !available
            ? "out_of_stock"
            : priceTBD
            ? "price_tbd"
            : variantOutOfStock
            ? "variant_out_of_stock"
            : reachedMaxInCart
            ? "cart_limit_reached"
            : "validation_blocked",
        },
      });
      return;
    }
    const { acceptedQty, cappedAt } = addToCart(product.id, qty, sizeArg, colorArg);
    if (cappedAt != null && acceptedQty < qty) {
      toast.warning(S.maxStockReached);
    }
    trackEvent("product_add_to_cart_success", {
      routeName: "product_detail",
      product: productPayload,
      metadata: { size: sizeArg || "", color: colorArg || "", quantity: acceptedQty || qty },
    });
    openCart();
  };

  const handleInquire = () => {
    const msg = S.inquireMsg
      .replace("{id}", product.id)
      .replace("{name}", name)
      .replace("{url}", shareUrl);
    trackEvent("whatsapp_click", {
      routeName: "product_detail",
      product: {
        id: product.id,
        title: name,
        category: product.category,
        sheinCategory: product.sheinCategory,
      },
      metadata: { source: "product_detail_inquiry" },
    });
    trackEvent("contact_click", {
      routeName: "product_detail",
      product: {
        id: product.id,
        title: name,
        category: product.category,
        sheinCategory: product.sheinCategory,
      },
      metadata: { source: "product_detail_inquiry", channel: "whatsapp" },
    });
    window.open(buildWhatsAppUrl(msg), "_blank", "noopener,noreferrer");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.warn("[ProductDetail] clipboard write failed:", err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: name, url: shareUrl });
      } catch (err) {
        // Native share was cancelled or rejected by the user — non-fatal.
        if (err?.name !== "AbortError") {
          console.warn("[ProductDetail] navigator.share failed:", err);
        }
      }
    } else {
      handleCopy();
    }
  };

  // Related products: SHEIN products show other SHEIN items in the same
  // sub-category; BOB products keep their original category-based logic.
  const related = (product.category === "shein" ? ALL_PRODUCTS : PRODUCTS)
    .filter((p) => {
      if (p.id === product.id) return false;
      if (isHiddenById(p.id)) return false;
      if (product.category === "shein") {
        return p.category === "shein" && p.sheinCategory === product.sheinCategory;
      }
      return p.category === product.category;
    })
    .slice(0, 4);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#FAFAFA] text-[#111] dark:bg-[#080808] dark:text-[#F6F7FB]" data-testid="product-detail-page">
      <Navbar />

      <section className="px-5 pt-24 md:px-8 md:pt-28">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => navigate(product.category === "shein" ? "/stock/shein" : "/stock")}
              className="inline-flex h-10 items-center gap-2 self-start rounded-full border border-black/10 bg-white px-4 text-sm font-medium text-[#111] shadow-[0_10px_24px_rgba(16,24,40,0.05)] transition-all hover:-translate-y-0.5 hover:border-[#F37021] hover:text-[#F37021] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
              data-testid="pd-back"
            >
              <ArrowLeft className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
              {S.backToStore}
            </button>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
              <button
                onClick={handleCopy}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border border-black/10 bg-white px-4 text-sm font-medium text-[#111] shadow-[0_10px_24px_rgba(16,24,40,0.05)] transition-all hover:-translate-y-0.5 hover:border-[#F37021] hover:text-[#F37021] sm:w-auto dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
                data-testid="pd-copy"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? S.copied : S.copyLink}</span>
              </button>
              <button
                onClick={handleShare}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-[#F37021] px-4 text-sm font-medium text-white shadow-[0_12px_26px_rgba(243,112,33,0.22)] transition-all hover:-translate-y-0.5 hover:bg-[#E06015] sm:w-auto"
                data-testid="pd-share"
              >
                <Share2 className="w-4 h-4" />
                {S.share}
              </button>
            </div>
          </div>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)] lg:gap-14">
            <div>
              <ImageGallery images={product.images} alt={name} testIdBase="pd-gallery" />
            </div>

            <div>
              <div className="rounded-[32px] border border-black/6 bg-white p-6 shadow-[0_24px_60px_rgba(16,24,40,0.07)] md:p-8 dark:border-white/10 dark:bg-[#121212]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-[#F7F7F8] px-2.5 py-1 text-[10px] font-bold tracking-[0.2em] uppercase text-[#888] dark:bg-white/10 dark:text-[#8FA1B8]">
                    #{product.id}
                  </span>
                  <span className="rounded-full bg-[#FFF1E7] px-2.5 py-1 text-[10px] font-bold tracking-[0.2em] uppercase text-[#F37021]">
                    {S.categories[product.category]}
                  </span>
                  {product.authentic && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#F3D571] to-[#C99A2E] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#1A0E00] shadow-sm"
                      data-testid="pd-authentic-badge"
                    >
                      <ShieldCheck className="w-3 h-3" strokeWidth={2.5} />
                      {S.badgeAuthentic}
                    </span>
                  )}
                  {isProductNew(product) && (
                    <span
                      className="rounded-full bg-[#F37021] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white"
                      data-testid={`pdp-new-badge-${product.id}`}
                    >
                      {S.badgeNew}
                    </span>
                  )}
                  {product.tags?.includes("limited") && (
                    <span className="rounded-full bg-[#111] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                      {S.badgeLimited}
                    </span>
                  )}
                </div>

                <h1
                  className="mt-4 font-display text-3xl font-bold leading-[1.05] text-[#111] sm:text-4xl md:text-5xl dark:text-white"
                  data-testid="pd-name"
                >
                  {name}
                </h1>

                {color && (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FAFAFA] px-4 py-2 text-sm text-[#555] dark:bg-[#121212] dark:text-[#BAC4D4]" data-testid="pd-color">
                    <span className="text-[11px] uppercase tracking-wider text-[#888] dark:text-[#8FA1B8]">{S.colorLabel}</span>
                    <span className="font-medium text-[#111] dark:text-white">{color}</span>
                  </div>
                )}

                {hasMultiColors && (
                  <div className="mt-6 rounded-[24px] border border-black/6 bg-[#FAFAFA] p-4 dark:border-white/10 dark:bg-[#121212]" data-testid="pd-color-selector">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#555] dark:text-[#BAC4D4]">
                        {S.selectColor}
                      </span>
                      <span className="text-[11px] text-[#888] dark:text-[#8FA1B8]">
                        {localizedColor(product, selectedColor, lang)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {colorList.map((cEn) => {
                        const cOut = isColorOutOfStock(product.id, cEn);
                        const isSelected = cEn === selectedColor;
                        const label = localizedColor(product, cEn, lang);
                        return (
                          <button
                            key={cEn}
                            type="button"
                            onClick={() => !cOut && setSelectedColor(cEn)}
                            disabled={cOut}
                            title={label}
                            className={`h-10 rounded-full border px-4 text-xs font-semibold transition-colors ${
                              isSelected
                                ? "border-[#111] bg-[#111] text-white"
                                : cOut
                                ? "cursor-not-allowed border-transparent bg-[#F1F1F1] text-[#BBB] line-through"
                                : "border-black/10 bg-white text-[#111] hover:border-[#111] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056]"
                            }`}
                            data-testid={`pd-color-${cEn.replace(/[^a-zA-Z0-9]/g, "-")}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-6 rounded-[24px] border border-black/6 bg-[#FFF9F4] p-5 dark:border-white/10 dark:bg-[#1B1B1B]" data-testid="pd-pricing">
                  <div className="flex flex-wrap items-baseline gap-3">
                    {priceTBD ? (
                      <span className="text-xl font-bold text-[#F37021] sm:text-2xl">
                        {S.priceTBDFull}
                      </span>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-[#F37021] sm:text-4xl">{formatPrice(discounted)}</span>
                        <span className="text-lg text-[#999] line-through dark:text-[#8FA1B8]">{formatPrice(product.originalPrice)}</span>
                        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-[#F37021] shadow-sm dark:bg-white/10">
                          -{discountPct}%
                        </span>
                      </>
                    )}
                  </div>

                  <div className="mt-4">
                    {priceTBD ? (
                      <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-[#F37021] dark:bg-white/10" data-testid="pd-status-tbd">
                        <span className="w-2 h-2 rounded-full bg-[#F37021]" />
                        {S.priceTBD}
                      </span>
                    ) : available ? (
                      <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-green-600 dark:bg-white/10" data-testid="pd-status-available">
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                        {S.available}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-red-600 dark:bg-white/10" data-testid="pd-status-out">
                        <span className="w-2 h-2 rounded-full bg-red-500" />
                        {S.outOfStock}
                      </span>
                    )}
                  </div>
                </div>

                {desc && (
                  <div className="mt-6 rounded-[24px] border border-black/6 bg-[#FAFAFA] p-5 dark:border-white/10 dark:bg-[#121212]">
                    <p className="whitespace-pre-line text-base leading-relaxed text-[#555] dark:text-[#BAC4D4]" data-testid="pd-description">
                      {desc}
                    </p>
                  </div>
                )}

                {hasSizes && (
                  <div className="mt-6 rounded-[24px] border border-black/6 bg-[#FAFAFA] p-5 dark:border-white/10 dark:bg-[#121212]" data-testid="pd-size-selector">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#555] dark:text-[#BAC4D4]">
                        {S.selectSize}
                      </span>
                      {variantStock > 0 && (
                        <span className="rounded-full bg-white px-3 py-1 text-[11px] text-[#888] dark:bg-white/10 dark:text-[#8FA1B8]" data-testid="pd-size-stock">
                          {variantStock} {variantStock === 1 ? S.pieceLeft : S.piecesLeft}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {product.sizes.map((s) => {
                        const sAvail = hasMultiColors
                          ? variantStockOf(product.id, s.label, selectedColor) > 0
                          : !isSizeOutOfStock(product.id, s.label);
                        const isSelected = s.label === selectedSize;
                        return (
                          <button
                            key={s.label}
                            type="button"
                            onClick={() => sAvail && setSelectedSize(s.label)}
                            disabled={!sAvail}
                            className={`h-11 min-w-[60px] rounded-2xl border px-4 text-sm font-medium transition-colors ${
                              isSelected
                                ? "border-[#111] bg-[#111] text-white"
                                : sAvail
                                ? "border-black/10 bg-white text-[#111] hover:border-[#111] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056]"
                                : "cursor-not-allowed border-transparent bg-[#F1F1F1] text-[#BBB] line-through"
                            }`}
                            data-testid={`pd-size-${s.label.replace(/[^a-zA-Z0-9]/g, "-")}`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!priceTBD && available && (
                  <div className="mt-6 rounded-[24px] border border-black/6 bg-[#FAFAFA] p-5 dark:border-white/10 dark:bg-[#121212]">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#555] dark:text-[#BAC4D4]">
                        {S.quantity}
                      </span>
                      <div className="inline-flex h-12 items-center rounded-full border border-black/10 bg-white shadow-[0_10px_24px_rgba(16,24,40,0.04)] dark:border-white/10 dark:bg-[#121212]">
                        <button
                          type="button"
                          onClick={() => setQty((q) => Math.max(1, q - 1))}
                          className="flex h-12 w-12 items-center justify-center text-[#111] transition-colors hover:text-[#F37021]"
                          aria-label="Decrease"
                          data-testid="pd-qty-dec"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="min-w-[36px] text-center text-base font-semibold" data-testid="pd-qty">{qty}</span>
                        <button
                          type="button"
                          onClick={() => {
                            if (qty >= remainingForVariant) {
                              toast.warning(S.maxStockReached);
                              return;
                            }
                            setQty((q) => q + 1);
                          }}
                          disabled={qty >= remainingForVariant}
                          className="flex h-12 w-12 items-center justify-center text-[#111] transition-colors hover:text-[#F37021] disabled:cursor-not-allowed disabled:text-[#CCC]"
                          aria-label="Increase"
                          data-testid="pd-qty-inc"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  {priceTBD ? (
                    <button
                      type="button"
                      disabled
                      className="inline-flex h-14 items-center justify-center gap-2 rounded-full border border-[#F37021]/30 bg-[#FFF3EA] px-7 font-semibold text-[#F37021] cursor-not-allowed"
                      data-testid="pd-disabled-tbd"
                    >
                      {S.priceTBDFull}
                    </button>
                  ) : canAdd ? (
                    <button
                      type="button"
                      onClick={handleAdd}
                      className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#F37021] px-7 font-semibold text-white shadow-[0_14px_30px_rgba(243,112,33,0.25)] transition-all hover:-translate-y-0.5 hover:bg-[#E06015]"
                      data-testid="pd-add-to-cart"
                    >
                      <ShoppingBag className="w-5 h-5" />
                      {S.addToCart}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#EEE] px-7 font-semibold text-[#999] cursor-not-allowed"
                      data-testid="pd-disabled-cta"
                    >
                      {S.outOfStock}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleInquire}
                    className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1EBE5C]"
                    data-testid="pd-whatsapp-inquire"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {S.inquireOnWhatsApp}
                  </button>
                </div>

                <div className="mt-7 grid grid-cols-1 gap-3 text-center sm:grid-cols-3">
                  {[S.trust1, S.trust2, S.trust3].map((label, i) => (
                    <div key={i} className="rounded-2xl border border-black/5 bg-[#FAFAFA] p-3 text-xs text-[#555]">
                      {label}
                    </div>
                  ))}
                </div>

                <PolicyCard className="mt-6" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="px-5 pb-20 pt-16 md:px-8 md:pb-28 md:pt-24">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6 flex items-end justify-between gap-4">
              <h2 className="font-display text-2xl font-bold text-[#111] sm:text-3xl">{S.relatedTitle}</h2>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-6 md:grid-cols-3 md:gap-x-6 md:gap-y-10 lg:grid-cols-4">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <FloatingWhatsApp />
      <CartDrawer />
      <StickyCartButton />
    </main>
  );
}
